/**
 * The customer's appointment reminder preference on the Your info step.
 *
 * The booking API accepts `notificationPreference` as `email | text | both`.
 * The designer's authored `notifDefault` predates that enum and still spells
 * the text channel `sms`, so every value is normalised here and `sms` is never
 * submitted.
 *
 * Two values are kept apart on purpose:
 *   authored default  the journey setting (`s.notifDefault`). Customer clicks
 *                     never write to it.
 *   customer choice   component state (`notifChoice`), empty until the customer
 *                     changes a checkbox. Empty means "still on the default",
 *                     so a default the author changes is followed until then.
 *
 * At least one channel always stays selected: unchecking the last one is
 * refused, never turned into an empty preference.
 */

export type ReminderPreference = "email" | "text" | "both";
export type ReminderChannel = "email" | "text";

/** A known preference value, or "" for anything else. Legacy `sms` reads as `text`. */
export function normalizeReminderPreference(value: unknown): ReminderPreference | "" {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "email" || v === "text" || v === "both") return v;
  if (v === "sms") return "text";
  return "";
}

/** The selection on screen: the customer's choice, else the authored default, else both. */
export function effectiveReminderPreference(choice: unknown, authoredDefault: unknown): ReminderPreference {
  return normalizeReminderPreference(choice) || normalizeReminderPreference(authoredDefault) || "both";
}

/** Which checkboxes are checked for a preference. */
export function reminderChannels(preference: unknown): { email: boolean; text: boolean } {
  const p = normalizeReminderPreference(preference) || "both";
  return { email: p !== "text", text: p !== "email" };
}

export interface ReminderToggleResult {
  preference: ReminderPreference;
  /** True when the change was refused because it would leave no channel selected. */
  refused: boolean;
}

/** Apply one checkbox change. Removing the only selected channel is refused. */
export function toggleReminderChannel(current: unknown, channel: ReminderChannel, checked: boolean): ReminderToggleResult {
  const now = reminderChannels(current);
  const next = { ...now, [channel]: !!checked };
  if (!next.email && !next.text) {
    return { preference: normalizeReminderPreference(current) || "both", refused: true };
  }
  return { preference: next.email && next.text ? "both" : next.email ? "email" : "text", refused: false };
}

/**
 * The value to submit. When the reminder choice is shown, the customer is
 * looking at a real selection (their own or the authored default they left
 * as is), so that exact selection is sent. When it is not shown, nothing is
 * sent and the tenant's channel configuration applies.
 */
export function reminderPreferenceForSubmit(shown: boolean, choice: unknown, authoredDefault: unknown): ReminderPreference | undefined {
  return shown ? effectiveReminderPreference(choice, authoredDefault) : undefined;
}
